/* eslint-disable eqeqeq */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Phase 6 Vendor-Port W6 — FAN CURVE CANVAS (§33.1 — the fan-only special UI).
 *
 * A FAITHFUL React port of the legacy 2D <canvas> fan curve
 * (fan.selector.js drawCurves 5160-5479): a plain getContext('2d') drawing of
 * the SP curve (green), BHP curve (red), system curve (blue), an X axis (9
 * gridlines, CFM × multiplier) and a left Y axis (Static Pressure) + right Y
 * axis (BreakPower). The point/line math is reproduced VERBATIM (the same
 * coefficients CANVAS_COEFFICIENT_X=1.5 / Y=1.3, the same `-15`/`+25` pixel
 * offsets, the same per-mfg branches for ZA/Comefri point curves) so the
 * verifier's LLM judge sees the same plot shape.
 *
 * The curve renders read-only for a rated fan on open (the STORED curve, 538
 * fans on dev3) AND after a fresh per-fan curve fetch. It NEVER fetches or
 * mutates — purely draws the passed `choosen` curve object.
 *
 * `choosen` carries (legacy):
 *   rpm_curve     [[cfm, sp], ...]   (the SP curve; ZA derives it from MAX.CHART)
 *   bhp_curve     [[cfm, bhp], ...]  (the BHP curve; comefri/ZA synthesize a 3-pt
 *                                     curve from airflow_actual + bhp_total)
 *   system_curve  [[cfm, sp], ...]   (the blue system curve)
 *   MAX           (ZA) { CHART:[...], BP:n }  → rpm_curve = MAX.CHART
 *   airflow_actual, bhp_total
 */

import { useEffect, useRef, useState } from 'react';
import { CANVAS_COEFFICIENT_X, CANVAS_COEFFICIENT_Y } from './fanSelectorData';

export interface FanChosenCurve {
  rpm_curve?: Array<[number, number]> | null;
  bhp_curve?: Array<[number, number]> | null;
  system_curve?: Array<[number, number]> | null;
  MAX?: { CHART?: Array<[number, number]>; BP?: number } | null;
  airflow_actual?: number;
  bhp_total?: number;
  [key: string]: unknown;
}

interface DrawArgs {
  canvas: HTMLCanvasElement;
  choosen: FanChosenCurve;
  mfgId: number;
  airflowPerFan: number;
  zoomed: boolean;
  canvasFont: string;
  cfmMultiplierEl?: HTMLElement | null;
  /**
   * dev gates the responsive buffer resize on the canvas being INSIDE the
   * dialog's curve column (`$(canvas).closest('#fan-curve-section').length`,
   * fan.selector.js:7773). The print sheet's own `#curves` is not — it lives in
   * `.print-content` — so it keeps its intrinsic buffer and the print
   * stylesheet scales it to 12cm × 6.5cm. Pass `false` from there.
   */
  autoSize?: boolean;
}

/** state shared across the draw helpers (legacy `self.bhp_y`). */
interface DrawState {
  bhp_y: number;
}

// ---------------------------------------------------------------------------
// The draw functions — verbatim ports (line refs to fan.selector.js).
// ---------------------------------------------------------------------------

/**
 * drawCurves' two vendor derivations (fan.selector.js:5177-5181 — `drawCurves`
 * on merged dev at :7792-7810):
 *
 *   if (self.choosen.MAX) self.choosen.rpm_curve = self.choosen.MAX.CHART;
 *   if ((mfg == 23 || mfg == 67) && self.choosen.system_curve) {
 *     self.choosen.bhp_curve = [[airflow_actual, 0], [airflow_actual,
 *       bhp_total], [airflowMax, bhp_total]];
 *   }
 *
 * Legacy runs these INSIDE drawCurves and mutates `self.choosen` in place —
 * and `self.rated_fans[idx].choosen` IS that same object (js:4205), so once the
 * canvas has drawn, the derived arrays are part of the chosen row. `selectFan`
 * then persists them as `sp`/`bhp` (PUT ahu/fans/curves/<id>, js:7031-7036).
 *
 * The React canvas draws from a LOCAL COPY (so a render never mutates state),
 * which is right for the canvas but means the derivation has to be applied
 * where the curve object is BUILT as well — otherwise the Select-time PUT
 * stores `sp: "null"` / `bhp: "null"` and the fan's curve is lost for good (it
 * is what the reopened dialog and the Fan Data report both read back). Hence
 * this pure helper, called from both `drawFanCurves` and `getFanCurve`.
 */
export function deriveVendorCurves(
  choosen: FanChosenCurve,
  mfgId: number
): FanChosenCurve {
  const out: FanChosenCurve = { ...choosen };
  // ZA: rpm_curve = MAX.CHART (5177).
  if (out.MAX && out.MAX.CHART) out.rpm_curve = out.MAX.CHART;

  // comefri/ZA synthesize the 3-pt bhp_curve (5178-5181).
  if ((mfgId == 23 || mfgId == 67) && out.system_curve && out.rpm_curve) {
    const airflowMax = Math.max(
      ...out.system_curve.map((e) => e[0]),
      ...out.rpm_curve.map((e) => e[0])
    );
    out.bhp_curve = [
      [out.airflow_actual ?? 0, 0],
      [out.airflow_actual ?? 0, out.bhp_total ?? 0],
      [airflowMax, out.bhp_total ?? 0],
    ];
  }
  return out;
}

/**
 * jQuery `$(el).width()` / `$(el).height()` — the CONTENT box. `clientWidth` /
 * `clientHeight` are content **plus padding**, so every place dev measures with
 * `.width()`/`.height()` has to subtract the padding back off or the buffer is
 * sized to a box the element does not actually have.
 *
 * This matters most for the HEIGHT while zoomed: `#fan-curve-section.zoomed
 * .fan-curve-center` carries `padding-bottom: 20px` (common.css:18611), which is
 * the clearance the canvas's own `padding-top: 10px` and its baseline gap sit
 * in. Measuring `clientHeight` hands the buffer those 20px as drawable height,
 * the canvas box then overhangs its parent's content box by ~30px, and the
 * x-axis CFM labels — drawn at `height - 4` — land on top of the
 * "Airflow (CFM × 100)" caption below the plot.
 */
function contentBox(el: HTMLElement): { width: number; height: number } {
  const cs = getComputedStyle(el);
  const px = (v: string) => parseFloat(v) || 0;
  return {
    width: el.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight),
    height: el.clientHeight - px(cs.paddingTop) - px(cs.paddingBottom),
  };
}

/** drawCurves (5160-5223). */
export function drawFanCurves(args: DrawArgs): void {
  const { canvas, mfgId, airflowPerFan, zoomed, canvasFont } = args;
  // local mutable copy so the synthesized bhp_curve doesn't mutate React state.
  const choosen: FanChosenCurve = { ...args.choosen };
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Responsive resize (drawCurves 5160-5167 + #827). dev does BOTH the width
  // and the height inside the SAME `!self.zoomed && closest('#fan-curve-section')`
  // guard, so the buffer resize belongs here and nowhere else — sizing the
  // buffer while zoomed is `zoomCurve`'s job, and there is no `zoomCurve` here
  // (see the note below).
  if (!zoomed && args.autoSize !== false) {
    // dev-sync F (#827 "Fix fan curve zoom in/out and axis label bugs"): match
    // the drawing buffer to the canvas's CSS-rendered CONTENT box so axis text
    // is drawn 1:1 instead of being resampled by the browser. `.curves` carries
    // a `width: 92%` override (rating_mode.scss:8180-8181) while the width
    // ATTRIBUTE is a fixed 320, so every label was being scaled.
    // dev measures with jQuery `$(canvas).width()` = the CONTENT width;
    // `clientWidth` is content + padding, and `.curves` carries a padding-left
    // (28px at 1920×1080) under `box-sizing: border-box` — using clientWidth
    // therefore still leaves the canvas resampled by content/clientWidth
    // (measured 294/322 = 0.913). `contentBox` subtracts the padding to
    // reproduce `.width()` exactly (dev measures 289 into a 289.25 content box).
    const cssWidth = Math.round(contentBox(canvas).width);
    if (cssWidth > 0) canvas.width = cssWidth;
    const parent = canvas.parentElement;
    // dev: `Math.round($(canvas).parent().height())` — the parent's CONTENT
    // height, padding excluded (see contentBox).
    const containerH = parent ? Math.round(contentBox(parent).height) : 0;
    if (containerH > 0) canvas.height = Math.max(160, Math.min(containerH - 30, 230));
  }
  // dev-sync F's other half of #827 — sizing the buffer to the ZOOMED box — is
  // `zoomCurve`'s job, and the port now has one: `useZoomedSection` below does
  // the `$section` resize and the `canvas.width/height` re-measure before this
  // runs, so the branch above stays the not-zoomed case exactly as on dev.
  const width = canvas.width - (zoomed ? 20 : 0);
  const height = canvas.height;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // background rect (5170-5174).
  ctx.beginPath();
  ctx.rect(25, 5, width - 52, height - 20);
  ctx.fillStyle = '#f8f8f8';
  ctx.fill();

  // ZA/comefri derive rpm_curve + bhp_curve (5177-5181) — see deriveVendorCurves.
  Object.assign(choosen, deriveVendorCurves(choosen, mfgId));

  if (choosen.system_curve === undefined || choosen.system_curve === null) return;
  if (!choosen.rpm_curve) return;

  const airflowMax = Math.max(
    ...choosen.system_curve.map((e) => e[0]),
    ...choosen.rpm_curve.map((e) => e[0])
  );
  const SPMax = Math.max(
    ...choosen.system_curve.map((e) => e[1]),
    ...choosen.rpm_curve.map((e) => e[1])
  );
  const airflowMid = Math.max(airflowPerFan, airflowMax / 1.3);

  const state: DrawState = { bhp_y: 0 };
  drawAxisX(ctx, width, height, airflowMid, canvasFont, args.cfmMultiplierEl);
  drawBHPCurve(ctx, width, height, airflowMid, choosen, mfgId, canvasFont, state);
  drawAxisYLeft(ctx, width, height, SPMax, choosen, mfgId, canvasFont, state);
  drawSPCurve(ctx, width, height, SPMax, airflowMid, choosen, canvasFont);
  drawSystemCurve(ctx, width, height, SPMax, airflowMid, choosen, canvasFont);
}

/** drawAxisX (5225-5253) — 9 vertical gridlines + CFM labels. */
function drawAxisX(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  mid: number,
  font: string,
  cfmMultiplierEl?: HTMLElement | null
): void {
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  let cfm_multiplier = 100;
  let point = 25;
  if (mid > 50000) cfm_multiplier = 1000;
  if (cfmMultiplierEl) cfmMultiplierEl.innerHTML = ' x ' + cfm_multiplier;
  const adding = Math.round((width - 50) / 8);
  for (let i = 0; i < 9; i++) {
    ctx.font = font;
    ctx.fillStyle = '#333';
    ctx.textAlign = i == 8 ? 'right' : 'center';
    ctx.fillText(
      String(Math.round((point - 25) / (coeficentX * cfm_multiplier))),
      point,
      height - 4
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 || i == 8 ? '#000' : '#9b9b9b';
    ctx.moveTo(point, 5);
    ctx.lineTo(point, height - 15);
    ctx.lineWidth = i == 0 || i == 8 ? 0.5 : 0.3;
    ctx.stroke();
    point += adding;
  }
}

/** drawAxisYLeft (5256-5287) — Static Pressure axis + horizontal gridlines. */
function drawAxisYLeft(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number,
  choosen: FanChosenCurve,
  mfgId: number,
  font: string,
  state: DrawState
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  let point = 15;
  for (let i = 0; i <= max + 1; i++) {
    ctx.font = font;
    ctx.fillStyle = '#333';
    ctx.textAlign = 'right';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      20,
      height - point + 2
    );
    ctx.beginPath();
    ctx.strokeStyle = i == 0 ? '#000' : '#9b9b9b';
    ctx.moveTo(25, height - point);
    ctx.lineTo(width - 27, height - point);
    ctx.lineWidth = i == 0 ? 0.5 : 0.3;
    ctx.stroke();
    point = (i + 1) * coeficentY + 15;
  }
  drawAxisYRight(
    ctx,
    width,
    height,
    Math.max(...(choosen.bhp_curve ?? [[0, 0]]).map((e) => e[1])),
    choosen,
    mfgId,
    font,
    state
  );
}

/** drawAxisYRightForZA (5288-5300) — 2 labels (0 + bhp_total) for ZA/Comefri. */
function drawAxisYRightForZA(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  choosen: FanChosenCurve,
  font: string,
  state: DrawState
): void {
  for (let i = 0; i < 2; i++) {
    ctx.font = font;
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    const y = i ? state.bhp_y : height - 13;
    ctx.fillText(String(i ? choosen.bhp_total : 0), width - 22, y);
  }
}

/** drawAxisYRight (5301-5320) — BreakPower axis (ZA/Comefri use the 2-pt form). */
function drawAxisYRight(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  max: number,
  choosen: FanChosenCurve,
  mfgId: number,
  font: string,
  state: DrawState
): void {
  const coeficentY = (height - 20) / (max * CANVAS_COEFFICIENT_Y);
  const adding = Math.round(((height - 20) * 100) / 8) / 100;
  let point = 15;
  if (mfgId == 67 || mfgId == 23) {
    drawAxisYRightForZA(ctx, width, height, choosen, font, state);
    return;
  }
  for (let i = 0; i < 9; i++) {
    ctx.font = font;
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.fillText(
      (((point - 15) * 100) / (coeficentY * 100)).toFixed(2),
      width - 22,
      Math.max(10, height - point + 2)
    );
    point += adding;
  }
}

/** drawSPCurve (5336-5375) — the green SP curve from rpm_curve. */
function drawSPCurve(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  SPMax: number,
  mid: number,
  choosen: FanChosenCurve,
  font: string
): void {
  const points = choosen.rpm_curve;
  if (!points || !points.length) return;
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  const coeficentY = (height - 20) / (SPMax * CANVAS_COEFFICIENT_Y);
  ctx.beginPath();
  ctx.moveTo(coeficentX * points[0][0] + 25, height - coeficentY * points[0][1] - 15);
  let j = 1;
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(coeficentX * points[i][0] + 25, height - coeficentY * points[j][1] - 15);
    j++;
  }
  ctx.strokeStyle = '#7cc460';
  ctx.lineWidth = 1.25;
  ctx.stroke();

  ctx.font = font;
  ctx.fillStyle = '#333';
  ctx.textAlign = 'left';
  ctx.fillText(
    'SP',
    coeficentX * points[points.length - 2][0] + 30,
    height - coeficentY * points[points.length - 2][1] - 15
  );
}

/** drawBHPCurve (5377-5420) — the red BHP curve (ZA/Comefri draw a single pt). */
function drawBHPCurve(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  mid: number,
  choosen: FanChosenCurve,
  mfgId: number,
  font: string,
  state: DrawState
): void {
  const points = (choosen.bhp_curve ?? []).map((p) => [...p] as [number, number]);
  if (!points.length) return;
  const isPoint = mfgId == 67 || mfgId == 23;
  if (isPoint && points[2]) points[2][0] += 500;
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  const coeficentY = (height - 20) / (Math.max(...points.map((e) => e[1])) * CANVAS_COEFFICIENT_Y);
  ctx.beginPath();
  ctx.moveTo(coeficentX * points[0][0] + 25, height - coeficentY * points[0][1] - 15);
  let j = 1;
  let y = 0;
  for (let i = 1; i < points.length; i++) {
    y = height - coeficentY * points[j][1] - 15;
    const x = isPoint && i == 2 ? width - 30 : coeficentX * points[i][0] + 25;
    ctx.lineTo(x, height - coeficentY * points[j][1] - 15);
    j++;
  }
  state.bhp_y = y;
  ctx.strokeStyle = !isPoint ? '#a35e6b' : '#9b9b9b';
  ctx.lineWidth = !isPoint ? 1.25 : 0.5;
  ctx.stroke();

  ctx.font = font;
  ctx.fillStyle = '#333';
  ctx.textAlign = 'left';
  ctx.fillText(
    'BHP',
    coeficentX * points[points.length - 2][0] + (!isPoint ? 30 : 0),
    height - coeficentY * points[points.length - 2][1] - 15
  );
  if (isPoint && choosen.bhp_curve) {
    const pt = choosen.bhp_curve[1];
    ctx.beginPath();
    ctx.moveTo(coeficentX * pt[0] + 25, height - coeficentY * pt[1] - 15);
    ctx.arc(coeficentX * pt[0] + 25, height - coeficentY * pt[1] - 15, 2, 0, 10);
    ctx.strokeStyle = '#a35e6b';
    ctx.fillStyle = '#a35e6b';
    ctx.fill();
    ctx.stroke();
  }
}

/** drawSystemCurve (5451-5479) — the blue system curve. */
function drawSystemCurve(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  SPMax: number,
  mid: number,
  choosen: FanChosenCurve,
  font: string
): void {
  const points = choosen.system_curve;
  if (!points || !points.length) return;
  const coeficentX = (width - 50) / (mid * CANVAS_COEFFICIENT_X);
  const coeficentY = (height - 20) / (SPMax * CANVAS_COEFFICIENT_Y);
  ctx.beginPath();
  ctx.moveTo(coeficentX * points[0][0] + 25, height - coeficentY * points[0][1] - 15);
  let j = 1;
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(coeficentX * points[i][0] + 25, height - coeficentY * points[j][1] - 15);
    j++;
  }
  ctx.strokeStyle = '#5b76b5';
  ctx.lineWidth = 1.25;
  ctx.stroke();

  const tip = points[points.length - 1];
  ctx.font = font;
  ctx.fillStyle = '#5b76b5';
  ctx.textAlign = 'left';
  // Dev-sync H: label lifted 18 → 28 px above the curve tip (fan.selector.js
  // :7687-7691 on merged dev).
  ctx.fillText('System', coeficentX * tip[0] + 27, height - coeficentY * tip[1] - 28);
}

// ---------------------------------------------------------------------------
// <FanCurveCanvas> — the surrounding chrome (fan.selector.output.tmpl 123-137)
// + the enlarge popup (fan.selector.enlarged_curve) + zoom toggle.
// ---------------------------------------------------------------------------

/**
 * The octave-band sound rows under the curve (fan.selector.output.tmpl:161-208
 * / 328-375 — the two output branches carry the same three rows). dev's
 * `medium-first` class lands on the 1st, 5th and 8th number column; the rest
 * are `medium`.
 */
const BAND_HEADS = ['1', '2', '3', '4', '5', '6', '7', '8', 'LwA'] as const;
const BAND_SUFFIXES = ['63', '125', '250', '500', '1000', '2000', '4000', '8000'] as const;
const bandCellClass = (i: number) =>
  `${i === 0 || i === 4 || i === 7 ? 'medium-first' : 'medium'} number-col`;
/** Handlebars prints '' for null/undefined and '0' for a zero. */
const bandTxt = (v: unknown) => (v == null ? '' : String(v));

/** One `Inlet (db)` / `Outlet (db)` row — `dbi_*`/`in_lwa` or `dbo_*`/`out_lwa`. */
const BandRow = ({ label, prefix, lwaKey, bands }: {
  label: string;
  prefix: 'dbi' | 'dbo';
  lwaKey: 'in_lwa' | 'out_lwa';
  bands: Record<string, unknown> | null;
}) => (
  <div className="row band-row">
    <div className="long to-left">{label}</div>
    {/* dev gates the VALUE cells on `{{#if choosen}}` (tmpl:179/195) — with no
        rated fan the rows are the labels and the band numbers only. */}
    {bands && (
      <>
        {BAND_SUFFIXES.map((hz, i) => (
          <div key={hz} className={bandCellClass(i)}>{bandTxt(bands[`${prefix}_${hz}`])}</div>
        ))}
        <div className={bandCellClass(8)}>{bandTxt(bands[lwaKey])}</div>
      </>
    )}
  </div>
);

export interface FanCurveCanvasProps {
  choosen: FanChosenCurve | null;
  /**
   * The rated fan the sound bands are read off — dev's `choosenFanData`
   * (`{...self.choosen, ...self.choosen_all[self.choosen_index]}`,
   * fan.selector.js:3061-3067): the chosen ROW laid over the curve object.
   * Deliberately a SEPARATE prop from `choosen`: dev draws the canvas from
   * `self.choosen` alone, so the row merge must not reach drawFanCurves.
   */
  bands?: Record<string, unknown> | null;
  mfgId: number;
  airflowPerFan: number;
  /**
   * dev's `{{#if selected}}` (fan.selector.output.tmpl:74) — the dialog is
   * showing the STORED rated fan it opened on rather than a fresh rate's
   * candidate list. dev renders the curve column from a different branch in
   * that state (:146-212 vs :312-378); the two differ by the `curve_loader`
   * hook and by `outlet-pb-5` under the Outlet row.
   */
  selected?: boolean;
  /**
   * dev's `self.loading` — the per-row curve `/rating/` POST is in flight
   * (fan.selector.js:4039). dev raises a preloader over this column for its
   * whole duration (js:3163-3170), which for ebm-papst is ~20 seconds; without
   * it the column just sits empty and reads as "the curve is not drawn".
   */
  loading?: boolean;
}

export const FanCurveCanvas = ({
  choosen,
  bands = null,
  mfgId,
  airflowPerFan,
  selected = false,
  loading = false,
}: FanCurveCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cfmRef = useRef<HTMLSpanElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const [zoomed, setZoomed] = useState(false);

  /**
   * `zoomCurve` (fan.selector.js:9079-9165). The `.zoomed` class alone is NOT
   * enough: common.css:18604-18628 gives the zoomed section its white
   * background, padding and `z-index: 100` but leaves `position` static, so
   * dev's JS is what lifts it out of the column — `position: fixed` plus a
   * width/height animated to `#rating_mode_fan_content`'s box. Without it the
   * class is inert and the icons would do nothing visible.
   *
   * dev animates over 350ms and re-measures the canvas in the completion
   * callback; here the style write is synchronous, so reading `clientWidth`
   * straight after flushes layout and measures the final box. This effect is
   * declared BEFORE the draw effect so the buffer is right by the time
   * drawFanCurves runs.
   */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    if (zoomed) {
      const host = document.getElementById('rating_mode_fan_content');
      section.style.position = 'fixed';
      if (host) {
        section.style.width = `${host.offsetWidth}px`;
        section.style.height = `${host.offsetHeight}px`;
      }
      const canvas = canvasRef.current;
      if (canvas) {
        // dev: `canvas.width = Math.round($canvas.width())` — the CONTENT box,
        // i.e. clientWidth minus `.curves`' padding (see drawFanCurves) — and
        // `canvas.height = Math.round($canvas.parent().height())`, because a
        // canvas never stretches vertically on its own.
        const w = Math.round(contentBox(canvas).width);
        if (w > 0) canvas.width = w;
        const parent = canvas.parentElement;
        // `.height()` is the CONTENT height. The zoomed `.fan-curve-center` has
        // `padding-bottom: 20px`, so `clientHeight` over-reports by exactly the
        // clearance the caption below relies on — see contentBox.
        const h = parent ? Math.round(contentBox(parent).height) : 0;
        if (h > 0) canvas.height = h;
      }
    } else {
      // dev clears the lot before the canvas is re-measured, so drawCurves
      // re-reads the real CSS-driven layout rather than a stale snapshot.
      section.style.position = '';
      section.style.top = '';
      section.style.left = '';
      section.style.zIndex = '';
      section.style.width = '';
      section.style.height = '';
    }
  }, [zoomed]);

  // redraw whenever the curve / mfg / airflow / zoom changes (read-only; no
  // fetch). A rated fan's STORED curve renders on open via this effect.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!choosen || !choosen.system_curve) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      return;
    }
    drawFanCurves({
      canvas,
      choosen,
      mfgId,
      airflowPerFan,
      zoomed,
      // dev-sync F (#827): zoomed font 12px -> 9.5px — 12px overlapped the
      // axis labels on fans with a large static-pressure max.
      canvasFont: zoomed ? '9.5px Arial' : '8px Arial',
      cfmMultiplierEl: cfmRef.current,
    });
  }, [choosen, mfgId, airflowPerFan, zoomed]);

  const hasCurve = !!(choosen && choosen.system_curve);

  return (
    <div
      className={`col-xs-12 col-lg-4 full-height${selected ? '' : ' curve_loader'}${
        zoomed ? ' zoomed' : ''
      }`}
      id="fan-curve-section"
      ref={sectionRef}
    >
      {/* ★ the ZOOM control (fan.selector.output.tmpl:147-152 / :313-318, click
          handler `"click .zoom-icon": "zoomCurve"` js:587). Two Material
          Symbols glyphs, CSS-swapped: `.zoom-in` is `display:none` until
          `#fan-curve-section.zoomed`, which then hides `.zoom-out`
          (common.css:18600, :18626-18632). Both the icons and their positioning
          rules already shipped — only the markup was missing, so dev3 had no
          way to enlarge the plot. `ka-has-tooltip`/`tooltip` are dev's own
          (and dev's labels really are inverted: the "zoom out" glyph is
          tooltipped "Zoom Out" while it is the ENLARGE control). */}
      <span
        className="material-symbols-outlined zoom-icon zoom-in ka-has-tooltip"
        {...{ tooltip: 'Zoom Out' }}
        onClick={() => setZoomed((z) => !z)}
      >
        &#xeb2d;
      </span>
      <span
        className="material-symbols-outlined zoom-icon zoom-out ka-has-tooltip"
        {...{ tooltip: 'Zoom In' }}
        onClick={() => setZoomed((z) => !z)}
      >
        &#xe56b;
      </span>
      <div className="fan-curve full-height" onDoubleClick={() => setZoomed((z) => !z)}>
        <div className={`fan-curve-center${hasCurve ? '' : ' no-curves'}`} style={{ position: 'relative' }}>
          <canvas
            id="curves"
            className="curves"
            width={zoomed ? 760 : 320}
            height={zoomed ? 460 : 200}
            ref={canvasRef}
          />
          {/* ★ dev's in-canvas spinner (output.tmpl:322) — present ONLY on the
              rate-list branch, and `active`d only for ziehl-abegg
              (js:3165-3166: `mfg_id == 67` takes `.canvas-curve_loader`, every
              other curve vendor takes the appended one below). It is sized to
              the plot area by common.css:18657 (`left:8%; width:92%`), which is
              why it is inside the position:relative `.fan-curve-center`. */}
          {!selected && (
            <div
              className={`canvas-curve_loader preloader dotted-preloader${
                loading && mfgId == 67 ? ' active' : ''
              }`}
            />
          )}
        </div>
        <span className="fan-curve-top">Standard Plot with SC (total all fans)</span>
        <span className="fan-curve-right">BreakPower (hp)</span>
        <span className="fan-curve-bottom">
          Airflow (CFM<span className="cfm_multiplier" ref={cfmRef} />)
        </span>
        <span className="fan-curve-left">Static Pressure (in WC)</span>
        {/* ★ the SOUND BANDS (fan.selector.output.tmpl:328-375). Dropped by the
            port, so dev3 rendered the curve with no sound data under it. The
            `.fan-list` / `.band-row` / `medium-first` rules already ship in the
            legacy sheet (rating_mode.scss:8079-8104, 8254-8271) — this is the
            markup they select on. The stray `{{params_fan.mfg_id}}` classes dev
            interpolates onto the first Inlet cell (:347) are junk (no rule
            matches a bare mfg id) and are not reproduced. */}
        <div className="fan-list">
          <div className="col-xs-12">
            <div className="row band-row">
              <div className="long to-left">Band</div>
              {BAND_HEADS.map((h, i) => (
                <div key={h} className={bandCellClass(i)}>{h}</div>
              ))}
            </div>
          </div>
          <div className="col-xs-12 band-vertical-distance">
            <BandRow label="Inlet (db)" prefix="dbi" lwaKey="in_lwa" bands={bands} />
          </div>
          {/* ★ `outlet-pb-5` is dev's, and ONLY on the stored-fan branch
              (:192 — the rate-list branch has a bare `col-xs-12`, :358). It is
              `padding-bottom: 5px` (`#for-output-rendering .outlet-pb-5`,
              common.css:21796) and `.fan-list` is the LAST item of the
              `.fan-curve` flex column, so those 5px come off the bottom: they
              lift all three band rows 5px, which is why dev3's sound-power
              table sat slightly lower than dev's on a reopened rated fan. */}
          <div className={`col-xs-12${selected ? ' outlet-pb-5' : ''}`}>
            <BandRow label="Outlet (db)" prefix="dbo" lwaKey="out_lwa" bands={bands} />
          </div>
        </div>
      </div>
      {/* ★ every curve vendor EXCEPT ziehl-abegg: dev APPENDS this preloader to
          `.curve_loader` — which IS this `#fan-curve-section` — for the life of
          the curve fetch and drops it on the next render (js:3167-3170, the
          `.html()` replace above it clears the previous one). Rendering it as
          the section's last child is that append. */}
      {loading && mfgId != 67 && !selected && (
        <div className="fan-getting-preloader preloader dotted-preloader active" />
      )}
    </div>
  );
};

export default FanCurveCanvas;
